import { Controller } from "@hotwired/stimulus"

/**
 * Image Gallery Controller
 *
 * Provides an interactive image gallery with:
 * - Main image display that's clickable for lightbox view
 * - Thumbnail navigation to switch between images
 * - Fullscreen lightbox with close on click/escape
 *
 * @example HTML structure
 *   <div data-controller="image-gallery">
 *     <img data-image-gallery-target="main" data-action="click->image-gallery#openLightbox" src="..." />
 *     <div data-image-gallery-target="thumbnails">
 *       <button data-action="image-gallery#selectImage" data-image-url="...">
 *         <img src="..." />
 *       </button>
 *     </div>
 *     <div data-image-gallery-target="lightbox" class="hidden">...</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["main", "thumbnails", "lightbox", "lightboxImage", "thumbnail"]

  connect() {
    // Bind escape key to close lightbox
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  /**
   * Selects an image from the thumbnails and displays it as the main image.
   *
   * @param {Event} event - Click event from thumbnail button
   */
  selectImage(event) {
    event.preventDefault()
    const button = event.currentTarget
    const imageUrl = button.dataset.imageUrl
    const fullUrl = button.dataset.fullUrl

    if (!imageUrl) return

    // Update main image
    if (this.hasMainTarget) {
      this.mainTarget.src = imageUrl
      this.mainTarget.dataset.fullUrl = fullUrl || imageUrl
    }

    // Update active thumbnail state
    this.thumbnailTargets.forEach(thumb => {
      thumb.classList.remove("ring-2", "ring-primary-2-500")
      thumb.classList.add("opacity-60")
    })
    button.classList.add("ring-2", "ring-primary-2-500")
    button.classList.remove("opacity-60")
  }

  /**
   * Opens the lightbox with the current main image.
   *
   * @param {Event} event - Click event from main image
   */
  openLightbox(event) {
    event.preventDefault()
    
    if (!this.hasLightboxTarget || !this.hasLightboxImageTarget) return

    // Get full resolution URL from data attribute or use current src
    const fullUrl = this.mainTarget.dataset.fullUrl || this.mainTarget.src
    this.lightboxImageTarget.src = fullUrl

    // Show lightbox
    this.lightboxTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  /**
   * Closes the lightbox when clicking the backdrop.
   *
   * @param {Event} event - Click event
   */
  closeLightbox(event) {
    // Close when clicking backdrop
    if (this.hasLightboxTarget) {
      this.lightboxTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  /**
   * Forces lightbox close (for close button).
   *
   * @param {Event} event - Click event
   */
  forceCloseLightbox(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.hasLightboxTarget) {
      this.lightboxTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  /**
   * Prevents closing when clicking the image itself.
   *
   * @param {Event} event - Click event
   */
  preventClose(event) {
    event.stopPropagation()
  }

  /**
   * Handles keyboard events for lightbox.
   *
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeydown(event) {
    if (event.key === "Escape" && this.hasLightboxTarget && !this.lightboxTarget.classList.contains("hidden")) {
      this.forceCloseLightbox()
    }
  }
}

